import { useState, useEffect, useCallback } from "react";
import { AlertTriangle, GitMerge, X, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";

const label = (e) => e.display_name && e.display_name !== e.name ? `${e.display_name} (${e.name})` : e.name;

const PairRow = ({ pair, onMerge, onDismiss, busy }) => (
  <div
    className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-500/30 bg-slate-900/40 px-3 py-2"
    data-testid={`duplicate-pair-${pair.a.id}-${pair.b.id}`}
  >
    <div className="flex-1 min-w-[220px] text-sm">
      <span className="font-semibold text-amber-100">{label(pair.a)}</span>
      <span className="text-slate-500 mx-2">↔</span>
      <span className="font-semibold text-amber-100">{label(pair.b)}</span>
      <div className="text-xs text-slate-400 mt-0.5">{pair.reason}</div>
    </div>
    <div className="flex items-center gap-1.5">
      <Button size="sm" variant="outline" disabled={busy} onClick={() => onMerge(pair.a, pair.b)}
        className="h-7 text-xs border-amber-500/50 text-amber-200 hover:bg-amber-500/10"
        data-testid={`merge-keep-a-${pair.a.id}`}>
        <GitMerge className="w-3 h-3 mr-1" /> Keep {pair.a.display_name || pair.a.name}
      </Button>
      <Button size="sm" variant="outline" disabled={busy} onClick={() => onMerge(pair.b, pair.a)}
        className="h-7 text-xs border-amber-500/50 text-amber-200 hover:bg-amber-500/10"
        data-testid={`merge-keep-b-${pair.b.id}`}>
        <GitMerge className="w-3 h-3 mr-1" /> Keep {pair.b.display_name || pair.b.name}
      </Button>
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => onDismiss(pair)}
        className="h-7 text-xs text-slate-400 hover:text-slate-200" data-testid={`dismiss-pair-${pair.a.id}`}>
        <X className="w-3 h-3 mr-1" /> Not the same person
      </Button>
    </div>
  </div>
);

export const DuplicateWarning = ({ onChanged }) => {
  const [pairs, setPairs] = useState([]);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/v2/employees/merge/candidates");
      setPairs(res.data?.candidates || []);
    } catch { setPairs([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const merge = async (survivor, duplicate) => {
    if (!window.confirm(`Merge "${duplicate.name}" into "${survivor.name}"?\n\n"${duplicate.name}" becomes an alias of "${survivor.name}" and any live snapshot rows move to that record.`)) return;
    setBusy(true);
    try {
      const res = await api.post("/v2/employees/merge", { survivor_id: survivor.id, duplicate_id: duplicate.id });
      toast.success(res.data?.message || `Merged into ${survivor.name}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally { setBusy(false); }
  };

  const dismiss = async (pair) => {
    setBusy(true);
    try {
      await api.post("/v2/employees/merge/dismiss", { a_id: pair.a.id, b_id: pair.b.id });
      setPairs((p) => p.filter((x) => x !== pair));
    } catch { toast.error("Could not dismiss"); }
    finally { setBusy(false); }
  };

  if (!pairs.length) return null;

  return (
    <div className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4" data-testid="duplicate-warning">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 text-left" data-testid="duplicate-warning-toggle">
        <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
        <div className="flex-1">
          <div className="font-semibold text-amber-100">
            {pairs.length} possible duplicate {pairs.length === 1 ? "record" : "records"}
          </div>
          <div className="text-xs text-amber-200/70">Merge before building a snapshot so one person doesn't appear twice or get an empty row.</div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-amber-300" /> : <ChevronDown className="w-4 h-4 text-amber-300" />}
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {pairs.map((p) => <PairRow key={`${p.a.id}-${p.b.id}`} pair={p} onMerge={merge} onDismiss={dismiss} busy={busy} />)}
        </div>
      )}
    </div>
  );
};
